import React, { useEffect, useRef, useState } from 'react';

import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import { ENUM_ROLE_USER } from 'constants/enum';
import { TEXT_COMMON, TEXT_HEADER } from 'constants/string/texts';
import useChangeRouter from 'hooks/useChangeRouter';
import { useClickOutside } from 'hooks/useClickOutside';
import Profile from 'pages/Profile/Profile';
import AppRoutes from 'routes/routes';
import { setIsAuthenticated } from 'store/actions/auth';
import { clearModal, setModal } from 'store/actions/common';
import { setModalProfile } from 'store/actions/profile';
import { clearLocalStorage } from 'utils/localStorage';

const noHeaderPaths = [AppRoutes.login];

const menus = [
  {
    label: TEXT_HEADER.SURVEY_LIST,
    path: AppRoutes.survey,
    paths: [AppRoutes.survey, AppRoutes.surveyEdit, AppRoutes.surveyCreate, AppRoutes.surveyView, AppRoutes.surveyTemplate],
    role: ENUM_ROLE_USER.USER,
  },
  {
    label: TEXT_HEADER.ANSWER_LIST,
    path: AppRoutes.report,
    paths: [AppRoutes.report, AppRoutes.reportView, AppRoutes.reportSDQ],
    role: ENUM_ROLE_USER.USER,
  },
  { label: TEXT_HEADER.USER_LIST, path: AppRoutes.users, paths: [AppRoutes.users], role: ENUM_ROLE_USER.ADMIN },
  { label: TEXT_HEADER.SAMPLE_VIDEO_LIST, path: AppRoutes.sampleVideo, paths: [AppRoutes.sampleVideo], role: ENUM_ROLE_USER.ADMIN },
];

const Header = () => {
  const dispatch = useDispatch();
  const { goto } = useChangeRouter();

  const location = useLocation();
  const profileRef = useRef(null);

  const { modal } = useSelector((state) => state.profile);
  const { isAuthenticated, me } = useSelector((state) => state.auth);

  const [openMenuProfile, setOpenMenuProfile] = useState(false);

  const handleLogout = () => {
    setOpenMenuProfile(false);

    const _handleLogout = () => {
      clearLocalStorage();
      dispatch(setIsAuthenticated(false));
    };

    dispatch(
      setModal({
        isOpen: true,
        title: TEXT_HEADER.MODAL_CONFIRM_LOGOUT,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        confirmText: TEXT_COMMON.CONFIRM_TEXT,
        onCancel: () => dispatch(clearModal()),
        onConfirm: () => {
          dispatch(clearModal());
          _handleLogout();
          setOpenMenuProfile(false);
        },
        classWrapModal: '!max-w-104',
      })
    );
  };

  const shouldShowHeader = !noHeaderPaths.includes(location.pathname);

  useEffect(() => {
    menus_profile[0].action = () => {
      setOpenMenuProfile(false);
      dispatch(setModalProfile({ isOpen: true }));
    };
    menus_profile[1].action = () => handleLogout();
  }, []);

  useClickOutside(profileRef, () => setOpenMenuProfile(false));

  const menus_profile = [
    { label: TEXT_HEADER.ACCOUNT_SETTING, action: () => dispatch(setModalProfile({ isOpen: true })) },
    // {
    //   label: TEXT_HEADER.CHANGE_LANGUAGE,
    //   action: null,
    //   children: [
    //     { label: TEXT_HEADER.JAPANESE, action: null },
    //     { label: TEXT_HEADER.VIETNAMESE, action: null },
    //   ],
    // },
    { label: TEXT_HEADER.LOG_OUT, action: handleLogout },
  ];

  return (
    <>
      {shouldShowHeader && (
        <header data-testid="header" id="header" className="bg-white h-header sticky top-0 z-50">
          <div className="h-full w-full border border-solid border-header flex-between-center px-8">
            <div className="flex items-center gap-10">
              <div onClick={() => goto(AppRoutes.home)} className="flex items-end cursor-pointer">
                <span className="text-2xl font-bold text-logo">{TEXT_COMMON.LOGO}</span>
                <span className="ml-2 font-bold text-sm text-logo">{TEXT_COMMON.LOGO_ATTACHED}</span>
              </div>

              {isAuthenticated && (
                <nav className="flex">
                  <ul className="flex space-x-2">
                    {menus.map(
                      (menu, index) =>
                        (menu.role === ENUM_ROLE_USER.USER || (menu.role === ENUM_ROLE_USER.ADMIN && me?.role === ENUM_ROLE_USER.ADMIN)) && (
                          <li data-testid={`menu-${index}`} key={`menu-${index}`}>
                            <div
                              onClick={() => goto(menu.path)}
                              className={
                                _.some(menu.paths, (path) => matchPath(path, location.pathname))
                                  ? 'text-sm text-primary-346 border-primary-346 py-3 px-5 border-b-2 cursor-pointer'
                                  : 'text-sm text-black/85 border-transparent py-3 px-5 border-b-2 cursor-pointer'
                              }
                            >
                              {menu.label}
                            </div>
                          </li>
                        )
                    )}
                  </ul>
                </nav>
              )}
            </div>

            {isAuthenticated && (
              <div data-testid="profile" className="relative" ref={profileRef}>
                <div className="flex items-center gap-1 cursor-pointer" onClick={() => setOpenMenuProfile(!openMenuProfile)}>
                  <img src={require('assets/resources/UserOutlined.svg').default} alt="user outlined" width={18} height={18} />
                  <span className="text-sm text-logo">{me?.email || ''}</span>
                  <button className={`focus:outline-none transition-transform ml-1 ${openMenuProfile ? 'rotate-180' : ''}`}>
                    <img src={require('assets/resources/CaretDownOutlined.svg').default} alt="caret down outlined" width={18} height={18} />
                  </button>
                </div>

                <div
                  data-testid="menu-profile"
                  className={`absolute right-0 w-52 py-1 bg-white rounded-sm shadow-soft-md animation-dropdown ${openMenuProfile ? 'show profile' : ''}`}
                >
                  <ul>
                    {menus_profile.map((menu, index) => (
                      <li
                        key={`menu-profile-${index}`}
                        onClick={menu.action}
                        className="px-3 py-1.5 cursor-pointer text-sm text-logo hover:bg-white-795 flex-between-center group relative"
                      >
                        {menu.label}
                        {menu.children && menu.children.length > 0 && (
                          <>
                            <img
                              className="group-hover:inline-block hidden"
                              src={require('assets/resources/ArrowRight.svg').default}
                              alt="arrow right outlined"
                              width={12}
                              height={12}
                            />
                            <ul className="absolute top-0 right-full py-1 min-w-28 bg-white rounded-sm shadow-soft-md hidden group-hover:block">
                              {menu.children.map((child, index) => (
                                <li className="px-3 py-[7px] hover:bg-white-795" key={`menu-profile-${index}`}>
                                  {child.label}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </header>
      )}
      {modal.isOpen && <Profile />}
    </>
  );
};

export default React.memo(Header);
